import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    FormGroup,
    FormControlLabel,
    Checkbox,
    TextField,
    Button,
    Alert,
    Snackbar,
    Divider,
    Card,
    CardContent,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress
} from '@mui/material';
import {
    CheckCircle as CheckCircleIcon,
    Assignment as AssignmentIcon,
    Gavel as GavelIcon,
    Security as SecurityIcon,
    Description as DescriptionIcon,
    CloudUpload as UploadIcon,
    Visibility as ViewIcon,
    Save as SaveIcon,
    Send as SendIcon,
    Warning as WarningIcon,
    GetApp as DownloadIcon,
    Payment as PaymentIcon,
    Lock as LockIcon,
    Error as ErrorIcon
} from '@mui/icons-material';
import FileUploadComponent from '../../../components/FileUploadComponent';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import authService from '../../../services/authService';
import paymentService from '../../../services/paymentService';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import FeesService from '../../../services/feesService';
import declarationService from '../../../services/declarationService';
import LaboratoryService from '../../../services/laboratoryService';
import s3Utils from '../../../utils/s3Utils';
import previewReportService from '../../../services/previewReportService';
import {
    insertDANC,
    updateDANC,
    getDANCById
} from '../../../services/apiCalibration';
import { encryptAppId, encryptText } from '../../../utils/encryption';

// Adequacy Check Component with DA NC Integration
const AdequacyCheckSection = React.memo(({
    sectionId,
    title,
    applicationStage,
    orgId,
    appId,
    adequacyChecks,
    handleAdequacyCheck,
    handleAdequacyRemarks,
    handleAdequacySubmit,
    loading,
    currentUser = 'System User',
    currentUserId = 0
}) => {
    // Local state for DA NC operations
    const [dancLoading, setDancLoading] = useState(false);
    const [dancError, setDancError] = useState('');
    const [dancSuccess, setDancSuccess] = useState('');
    const [existingDANCRecord, setExistingDANCRecord] = useState(null);
    const [dataLoaded, setDataLoaded] = useState(false);
    

    // Load existing DA NC data on component mount
    useEffect(() => {
        const loadExistingDANCData = async () => {
            if (!orgId || !appId) return;

            try {
                setDancLoading(true);
                const response = await getDANCById(orgId, appId, title);

                if (response.data.success && response.data.data) {
                    setExistingDANCRecord(response.data.data);

                    // Bind DA NC data to adequacy check fields
                    if (response.data.data.InformationStatus) {
                        handleAdequacyCheck(sectionId, response.data.data.InformationStatus);
                    }

                    const remarks = response.data.data.InformationProvided || response.data.data.Observation || '';
                    if (remarks) {
                        handleAdequacyRemarks(sectionId, remarks);
                    }
                }
                setDataLoaded(true);
            } catch (error) {
                setDataLoaded(true);
            } finally {
                setDancLoading(false);
            }
        };

        if (!dataLoaded) {
            loadExistingDANCData();
        }
    }, [orgId, appId, title, sectionId, dataLoaded, handleAdequacyCheck, handleAdequacyRemarks]);

    // Clear messages after timeout
    useEffect(() => {
        if (dancError || dancSuccess) {
            const timer = setTimeout(() => {
                setDancError('');
                setDancSuccess('');
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [dancError, dancSuccess]);

    // Only show if applicationStage is 18, 18b or 18a
    if (applicationStage !== "18" && applicationStage !== "18a" && applicationStage !== "18b") return null;

    // Check if adequacy checks should be read-only (stage 18a)
    const isAdequacyReadOnly = applicationStage === "18a";

    const adequacy = adequacyChecks[sectionId] || { status: '', remarks: '' };

    // Standard status change handler
    const handleStatusChange = (status) => {
        handleAdequacyCheck(sectionId, status);
    };

    // Standard remarks change handler
    const handleRemarksChange = (remarks) => {
        handleAdequacyRemarks(sectionId, remarks);
    };

    // Enhanced submit handler with DA NC API call
    const handleSubmit = async () => {
        try {
            setDancLoading(true);
            setDancError('');
            setDancSuccess('');

            // First submit the adequacy check
            await handleAdequacySubmit(sectionId);

            // Then create/update DA NC record based on status
            if (adequacy.status) {
                if (existingDANCRecord) {
                    // Update existing record
                    try {
                        const updateData = {
                            subsectionName: sectionId,
                            informationStatus: adequacy.status,
                            observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            isactive: 1
                        };

                        const response = await updateDANC(orgId, appId, title, updateData);

                        if (response.success) {
                            setExistingDANCRecord(response.data);
                        } else {
                            throw new Error(response.message || 'Update failed');
                        }
                    } catch (updateError) {
                        console.error('DA-NC update failed:', updateError);
                    }
                } else {
                    // Create new record  
                    try {
                        const dancData = {
                            Orgid: parseInt(orgId),
                            Appid: parseInt(appId),
                            SectionName: title,
                            SubsectionName: sectionId,
                            InformationProvided: adequacy.remarks || '',
                            InformationStatus: adequacy.status,
                            Observation: adequacy.status === 'Inadequate'
                                ? (adequacy.remarks || 'Document adequacy marked as Inadequate')
                                : 'Document adequacy check completed',
                            Insertedby: currentUserId,
                            NCtype: 'Document Adequacy'
                        };

                        const response = await insertDANC(dancData);

                        if (response.data.success) {
                            setExistingDANCRecord(response.data.data);
                            setDancSuccess('Record created successfully');
                        } else {
                            throw new Error(response.message || 'Insert failed');
                        }
                    } catch (insertError) {
                        console.error('DA-NC creation failed:', insertError);
                        setDancError('Failed to create Record: ' + insertError.message);
                    }
                }
            }
        } catch (error) {
            console.error('Error submitting adequacy check:', error);
            setDancError('Error submitting adequacy check: ' + error.message);
        } finally {
            setDancLoading(false);
        }
    };

    return (
        <Box sx={{
            mb: 3,
            p: 3,
            backgroundColor: '#f8f9fa',
            borderRadius: 2,
            border: '2px solid #e9ecef',
            borderLeft: '4px solid #007bff'
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                }}>
                    📋 Adequacy Check - {title}
                    {isAdequacyReadOnly && (
                        <Chip
                            label="🔒 Read Only"
                            size="small"
                            sx={{
                                bgcolor: '#f5f5f5',
                                color: '#757575',
                                fontWeight: 'bold',
                                fontSize: '0.7rem'
                            }}
                        />
                    )}
                </Typography>

                {(adequacy.status || existingDANCRecord) && (
                    <Chip
                        label={`DA-NC: ${adequacy.status || existingDANCRecord?.InformationStatus || 'Active'}`}
                        size="small"
                        sx={{
                            bgcolor: (adequacy.status || existingDANCRecord?.InformationStatus) === 'Adequate' ? '#28a745' : '#dc3545',
                            color: 'white',
                            fontWeight: 'bold'
                        }}
                    />
                )}
            </Box>

            {/* DA NC Error/Success Messages */}
            {dancError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDancError('')}>
                    {dancError}
                </Alert>
            )}

            {dancSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDancSuccess('')}>
                    {dancSuccess}
                </Alert>
            )}

            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            borderRadius: 2,
                            '&:hover': {
                                boxShadow: '0 2px 8px rgba(0,123,255,0.15)',
                            },
                            '&.Mui-focused': {
                                boxShadow: '0 4px 12px rgba(0,123,255,0.25)',
                            }
                        }
                    }}>
                        <InputLabel>Document Status</InputLabel>
                        <Select
                            value={adequacy.status}
                            onChange={isAdequacyReadOnly ? undefined : (e) => handleStatusChange(e.target.value)}
                            label="Document Status"
                            disabled={isAdequacyReadOnly}
                            sx={isAdequacyReadOnly ? {
                                backgroundColor: '#f5f5f5',
                                cursor: 'not-allowed',
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#e0e0e0',
                                }
                            } : undefined}
                        >
                            <MenuItem value="">-- Select Status --</MenuItem>
                            <MenuItem value="Adequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CheckCircleIcon sx={{ color: '#28a745', fontSize: '1rem' }} />
                                    Adequate
                                </Box>
                            </MenuItem>
                            <MenuItem value="Inadequate">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ErrorIcon sx={{ color: '#dc3545', fontSize: '1rem' }} />
                                    Inadequate
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                {adequacy.status === 'Inadequate' && (
                    <Grid item xs={12} md={8}>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label={isAdequacyReadOnly ? "Remarks (View Only)" : "Remarks (Required)"}
                            placeholder={isAdequacyReadOnly ? "View Only (Read-only)" : "Please provide specific comments about the inadequacies..."}
                            value={adequacy.remarks || ''}
                            onChange={isAdequacyReadOnly ? undefined : (e) => handleRemarksChange(e.target.value)}
                            required={!isAdequacyReadOnly}
                            variant="outlined"
                            InputProps={isAdequacyReadOnly ? {
                                readOnly: true,
                                style: {
                                    backgroundColor: '#f5f5f5',
                                    cursor: 'not-allowed'
                                }
                            } : undefined}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: isAdequacyReadOnly ? '#f5f5f5' : 'white',
                                    borderRadius: 2,
                                    cursor: isAdequacyReadOnly ? 'not-allowed' : 'text',
                                    '&:hover': {
                                        boxShadow: isAdequacyReadOnly ? 'none' : '0 2px 8px rgba(220,53,69,0.15)',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: isAdequacyReadOnly ? 'none' : '0 4px 12px rgba(220,53,69,0.25)',
                                    }
                                }
                            }}
                        />
                        <Typography variant="caption" sx={{
                            color: '#6c757d',
                            display: 'block',
                            mt: 1,
                            fontStyle: 'italic'
                        }}>
                            {isAdequacyReadOnly
                                ? "Adequacy check is in read-only mode for stage 18a."
                                : "Please specify what needs to be corrected or improved."
                            }
                        </Typography>
                    </Grid>
                )}
            </Grid>

            {adequacy.status && (
                <Box sx={{ mt: 2 }}>
                    <Alert
                        severity={existingDANCRecord
                            ? (adequacy.status === 'Adequate' ? 'info' : 'warning')
                            : (adequacy.status === 'Adequate' ? 'success' : 'warning')
                        }
                        sx={{
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '1.2rem'
                            }
                        }}
                    >
                        {isAdequacyReadOnly
                            ? '🔒 Adequacy check is in read-only mode for stage 18a. No modifications can be made.'
                            : (existingDANCRecord
                                ? (adequacy.status === 'Adequate'
                                    ? `✓ Record exists. Click Update to modify.`
                                    : `⚠ Record exists. Click Update to modify.`)
                                : (adequacy.status === 'Adequate'
                                    ? '✓ This section is marked as Adequate. Click Submit to create Record.'
                                    : '⚠ Please address the remarks above and submit to create Record.'))}
                    </Alert>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            size="medium"
                            onClick={handleSubmit}
                            disabled={
                                isAdequacyReadOnly ||
                                !adequacy.status ||
                                (adequacy.status === 'Inadequate' && !adequacy.remarks?.trim()) ||
                                loading ||
                                dancLoading
                            }
                            sx={{
                                background: existingDANCRecord
                                    ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)'
                                    : (adequacy.status === 'Adequate'
                                        ? 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'
                                        : 'linear-gradient(135deg, #ffc107 0%, #fd7e14 100%)'),
                                color: 'white',
                                fontWeight: 600,
                                px: 3,
                                py: 1,
                                borderRadius: 2,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                                },
                                '&:disabled': {
                                    background: '#e9ecef',
                                    color: '#6c757d',
                                    transform: 'none',
                                    boxShadow: 'none'
                                }
                            }}
                        >
                            {(loading || dancLoading) ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} sx={{ color: 'white' }} />
                                    {existingDANCRecord ? 'Updating...' : 'Submitting...'}
                                </Box>
                            ) : (
                                <>📤 {existingDANCRecord ? 'Update Adequacy Check' : 'Submit Adequacy Check'}</>
                            )}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
});

const DeclarationSection = ({ data, onUpdate }) => {
    const navigate = useNavigate();

    // Router hooks for encrypted parameters
    const { orgId: encryptedOrgId, appId: encryptedAppId } = useParams();
    const location = useLocation();
    const [canProceedToPay, setCanProceedToPay] = useState(false);

    // Extract encrypted parameters from URL query params or route params
    const getOrgId = useCallback(() => {
        try {
            // First try to get from URL query parameters
            const urlParams = new URLSearchParams(location.search);
            const orgIdParam = urlParams.get('orgId');
            if (orgIdParam) {
                return decryptText(orgIdParam);
            }

            // Then try to get from route parameters
            if (encryptedOrgId) {
                return decryptText(encryptedOrgId);
            }

            return null;
        } catch (error) {
            console.error('DeclarationSection - Error decrypting orgId:', error);
            return null;
        }
    }, [encryptedOrgId, location.search]);

    const getAppId = useCallback(() => {
        try {
            // First try to get from URL query parameters
            const urlParams = new URLSearchParams(location.search);
            const appIdParam = urlParams.get('appId');
            if (appIdParam) {
                return decryptAppId(appIdParam);
            }

            // Then try to get from route parameters
            if (encryptedAppId) {
                return decryptAppId(encryptedAppId);
            }

            return null;
        } catch (error) {
            console.error('DeclarationSection - Error decrypting appId:', error);
            return null;
        }
    }, [encryptedAppId, location.search]);

    // State management
    const [declarationData, setDeclarationData] = useState({
        // Authorized representative details
        representativeName: '',
        representativeTitle: '',

        // Additional declaration
        termchk: false,
        finalDeclarationAgreed: false,

        // File upload
        shareholderDirectorFile: null
    });

    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'info'
    });

    const [submitDialog, setSubmitDialog] = useState(false);
    const [passwordDialog, setPasswordDialog] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordError, setPasswordError] = useState('');
    const [loading, setLoading] = useState(false);
    const [feesData, setFeesData] = useState(null);
    const [feesLoading, setFeesLoading] = useState(false);

    // Simplified state management
    const [fieldsDisabled, setFieldsDisabled] = useState(false);
    const [paymentStatus, setPaymentStatus] = useState(null); // null, 'failed', 'success'
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [showPaymentButtons, setShowPaymentButtons] = useState(false);
    const [showSubmitButton, setShowSubmitButton] = useState(false);
    // GST Exemption state
    const [gstExemptionStatus, setGstExemptionStatus] = useState(null); // null, 'approved', 'pending', 'not_requested'
    const [gstExemptionMessage, setGstExemptionMessage] = useState('');

    // Add loading flag to prevent multiple calls
    const [initialLoadComplete, setInitialLoadComplete] = useState(false);
    const [applicationStage, setApplicationStage] = useState(null);

    // Adequacy Check States
    const [adequacyChecks, setAdequacyChecks] = useState({});
    const [adequacyLoading, setAdequacyLoading] = useState(false);
    const [isGstExemption, setIsGstExemption] = useState(false);

    // Adequacy Check Handlers
    const handleAdequacyCheck = (sectionId, status) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...(prev[sectionId] || {}),
                status: status
            }
        }));
    };

    const handleAdequacyRemarks = (sectionId, remarks) => {
        setAdequacyChecks(prev => ({
            ...prev,
            [sectionId]: {
                ...(prev[sectionId] || {}),
                remarks: remarks
            }
        }));
    };

    const handleAdequacySubmit = async (sectionId) => {
        setAdequacyLoading(true);
        return new Promise((resolve) => {
            setTimeout(() => {
                setAdequacyChecks(prev => ({
                    ...prev,
                    [sectionId]: {
                        ...prev[sectionId],
                        isSubmitted: true,
                        submittedAt: new Date().toISOString()
                    }
                }));
                setAdequacyLoading(false);
                resolve();
            }, 500);
        });
    };

    // Get current application stage
    const getCurrentApplicationStage = async () => {
        try {
            const appId = getAppId();
            if (appId) {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/application-stage/${appId}`);
                const result = await response.json();

                if (result.success && result.stage) {
                    setApplicationStage(result.stage);

                } else {
                    setApplicationStage(null); // Stage not found, set to null

                }
            } else {
                setApplicationStage(null); // No appId, set to null
            }
        } catch (error) {
            console.error('Error determining application stage:', error);
            // Use null as default if API fails
            setApplicationStage(null);
        }
    };

    const labbasicdetails = async () => {
        try {
            const orgId = getOrgId();
            const appId = getAppId();
            if (!orgId || !appId) return;

            const response = await fetch(`${process.env.REACT_APP_API_URL}/laboratory/details/${orgId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const result = await response.json();

            if (result.success && result.data) {

                setIsGstExemption(result.data.GSTExemption === true);

            } else {
                console.error('Error fetching laboratory details:', result.message);
            }
        } catch (error) {
            console.error('Error fetching laboratory details:', error);
        }
    };

    const checkGSTExemptionStatus = async () => {
        try {
            const appId = getAppId();
            if (!appId) {
                setGstExemptionStatus('not_requested');

                return;
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/gst-exemption/check-status/${appId}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            const result = await response.json();

            if (result.success && result.data && result.data.length > 0) {
                const exemptionRecord = result.data[0]; // Get the first record
                const isApproved = exemptionRecord.isapproved;
                const remarks = exemptionRecord.Remarks || '';

                if (isApproved === 1) {
                    // Approved
                    setGstExemptionStatus('approved');
                    setGstExemptionMessage('GST exemption approved');
                } else if (isApproved === 0) {
                    // Pending
                    setGstExemptionStatus('pending');
                    setGstExemptionMessage('Your GST exemption is pending in accounts section for approval');
                } else if (isApproved === 2) {
                    // Rejected
                    setGstExemptionStatus('rejected');
                    setGstExemptionMessage(`GST exemption has been rejected. Remarks: ${remarks}`);
                } else {
                    // Unknown status
                    setGstExemptionStatus('pending');
                    setGstExemptionMessage('GST exemption status is unknown. Please contact support.');
                }
            } else if (result.success && (!result.data || result.data.length === 0)) {
                // No GST exemption request found
                setGstExemptionStatus('not_requested');
                setGstExemptionMessage('No GST exemption request found');
            } else {
                // API error
                setGstExemptionStatus('pending');
                setGstExemptionMessage('Error checking GST exemption status');
            }
        } catch (error) {
            console.error('Error checking GST exemption status:', error);
            setGstExemptionStatus('pending');
            setGstExemptionMessage('Error checking GST exemption status');
        }
    };

    // Load declaration data and check payment status on page load
    useEffect(() => {
        const initializeComponent = async () => {

            await loadDeclarationData();
            await checkGSTExemptionStatus();
            await getCurrentApplicationStage();
            await labbasicdetails();

            // Check payment status immediately after loading other data
            await checkPaymentStatus();
            await checkPaymentCount()

            setInitialLoadComplete(true);
        };

        initializeComponent();
    }, [applicationStage]);

    // Load declaration data
    const loadDeclarationData = async () => {
        try {
            const orgId = getOrgId();
            const appId = getAppId();

            if (!orgId || !appId) return;

            // Get declaration data
            const response = await LaboratoryService.getDeclarationData(orgId, appId);

            if (response.success && response.data) {

                // Handle the case where both name and title are in DeclNameOrg
                let repName = '';
                let repTitle = '';

                if (response.data.DeclNameOrg) {
                    // Split the combined field "Ayan, head of laboratory"
                    const parts = response.data.DeclNameOrg.split(',').map(part => part.trim());
                    repName = parts[0] || '';
                    repTitle = parts[1] || '';
                } else {
                    // Fallback to separate fields if they exist
                    repName = response.data.representativeName || '';
                    repTitle = response.data.representativeTitle || '';
                }

                // Process shareholderDirectorFile with S3 utils (matching Laboratory section)
                let processedFile = null;
                if (response.data.shareholderDirectorFile) {
                    // If it's a string (filename), process it to get signed URL
                    if (typeof response.data.shareholderDirectorFile === 'string') {
                        processedFile = await s3Utils.processForm153FileData(
                            response.data.shareholderDirectorFile,
                            'shareholderDirectorFile'
                        );
                    } else {
                        // If it's already an object, use it as-is
                        processedFile = response.data.shareholderDirectorFile;
                    }
                }

                // Update form fields
                setDeclarationData({
                    representativeName: repName,
                    representativeTitle: repTitle,
                    shareholderDirectorFile: processedFile,
                    termchk: response.data.termchk || false,
                    finalDeclarationAgreed: response.data.finalDeclarationAgreed || false
                });

                // Create modified data object for button visibility check
                const modifiedData = {
                    representativeName: repName,
                    representativeTitle: repTitle,
                    shareholderDirectorFile: processedFile,
                    termchk: response.data.termchk || false,
                    finalDeclarationAgreed: response.data.finalDeclarationAgreed || false
                };

                // Check if all fields are filled and determine button state
                checkButtonVisibility(modifiedData);

                // Check payment status with loaded data
                await checkPaymentStatus(modifiedData);
            } else {
                // No existing data - show save button disabled initially
                setShowSaveButton(true);
                // Check payment status with empty data
                await checkPaymentStatus();
            }

        } catch (error) {

            setShowSaveButton(true);
            await checkPaymentStatus();
        }
    };

    // Check what buttons to show based on declaration data
    const checkButtonVisibility = (data = declarationData) => {
        const hasAllFields = data.representativeName &&
            data.representativeTitle &&
            data.shareholderDirectorFile &&
            data.finalDeclarationAgreed &&
            data.termchk;

        if (hasAllFields) {
            // All fields filled - disable fields and hide save button
            console.log('✅ All fields complete - disabling form fields');
            setFieldsDisabled(true);
            setShowSaveButton(false);
            // Payment buttons and Submit button will be determined by payment status check
        } else {
            // Some fields missing - show save button, enable fields
            console.log('❌ Missing fields - enabling form for editing');
            setShowSaveButton(true);
            setFieldsDisabled(false);
            setShowPaymentButtons(false);
            // Don't set Submit button here - let checkPaymentStatus handle it
        }
    };

    // Check payment status from PaymentGateWayTracking table
    const checkPaymentStatus = async (dataToCheck) => {
        // Always use current state if no data provided
        const currentData = dataToCheck || declarationData;

        console.log('🔄 checkPaymentStatus called with data:', currentData);

        // Skip if we don't have meaningful data yet (prevent premature hiding of buttons)
        const hasAnyData = currentData.representativeName || currentData.representativeTitle || currentData.shareholderDirectorFile;
        if (!dataToCheck && !hasAnyData) {
            console.log('⏭️ Skipping payment check - no meaningful data yet');
            return;
        }

        try {
            const orgId = getOrgId();
            const appId = getAppId();

            if (!orgId || !appId) {

                return;
            }


            const response = await paymentService.checkPaymentStatus(orgId, appId);

            if (response.success && response.data && Array.isArray(response.data) && response.data.length > 0) {
                console.log('Payment data received:', response.data);

                // Check if any payment has success status
                const hasSuccessfulPayment = response.data.some(payment => {
                    const hasOrderStatus = payment.order_status?.toLowerCase() === 'success' || payment.order_status === 'Success';

                    console.log(`Payment ${payment.Id}: order_status=${payment.order_status}`);

                    return hasOrderStatus
                });

                console.log('hasSuccessfulPayment:', hasSuccessfulPayment);

                // Check if any payment has failed status
                const hasFailedPayment = response.data.some(payment =>
                    payment.order_status?.toLowerCase() === 'failed'
                );
                if (hasSuccessfulPayment == true) {
                    console.log('Successful payment found:', response.data);
                    setPaymentStatus('success');
                    setShowPaymentButtons(false);

                    // Check if all fields are complete for submit button (use current data to avoid timing issues)
                    const hasAllFields = currentData.representativeName &&
                        currentData.representativeTitle &&
                        currentData.shareholderDirectorFile &&
                        currentData.finalDeclarationAgreed &&
                        currentData.termchk;

                    console.log('Checking submit button conditions with data:', currentData);
                    console.log('🔍 Final Submit Button Conditions:');
                    console.log('hasAllFields:', hasAllFields);
                    console.log('fieldsDisabled:', fieldsDisabled);
                    console.log('applicationStage:', applicationStage);
                    console.log('applicationStage === "":', applicationStage === "");
                    console.log('applicationStage === null:', applicationStage === null);
                    console.log('Stage condition result:', (applicationStage === "" || applicationStage === null));
                    console.log('Final condition result:', ((hasAllFields || fieldsDisabled) && (applicationStage === "" || applicationStage === null)));

                    if ((hasAllFields || fieldsDisabled) && (applicationStage === "" || applicationStage === null)) {
                        console.log('✅ SHOWING Final Submit Button');
                        setShowSubmitButton(true);
                    } else {
                        console.log('❌ HIDING Final Submit Button');
                        setShowSubmitButton(false);
                    }
                } else if (hasFailedPayment === true) {
                    console.log('Failed payment found:', response.data);
                    setPaymentStatus('failed');

                    // Check if all fields are complete (use current data to avoid timing issues)
                    const hasAllFields = currentData.representativeName &&
                        currentData.representativeTitle &&
                        currentData.shareholderDirectorFile &&
                        currentData.finalDeclarationAgreed &&
                        currentData.termchk;

                    if (hasAllFields || fieldsDisabled) {
                        setShowPaymentButtons(true);
                    } else {
                        setShowPaymentButtons(false);
                    }
                    setShowSubmitButton(false);
                } else {

                    setPaymentStatus('pending');
                    setShowPaymentButtons(false);
                    setShowSubmitButton(false);
                }
            } else {

                setPaymentStatus(null);

                // Check if all fields are complete (use current data to avoid timing issues)
                const hasAllFields = currentData.representativeName &&
                    currentData.representativeTitle &&
                    currentData.shareholderDirectorFile &&
                    currentData.finalDeclarationAgreed &&
                    currentData.termchk;

                if (hasAllFields || fieldsDisabled) {

                    setShowPaymentButtons(true);
                } else {

                }
                setShowSubmitButton(false);
            }
        } catch (error) {
            console.error('Error checking payment status:', error);
            setPaymentStatus(null);

            // Check if all fields are complete (use current data to avoid timing issues)
            const hasAllFields = currentData.representativeName &&
                currentData.representativeTitle &&
                currentData.shareholderDirectorFile &&
                currentData.finalDeclarationAgreed &&
                currentData.termchk;

            if (hasAllFields || fieldsDisabled) {
                setShowPaymentButtons(true);
            }
        }
    };


    const checkPaymentCount = async () => {
        try {
            const orgId = getOrgId();
            const appId = getAppId();

            if (!orgId || !appId) return;

            const response = await paymentService.checkPaymentCount(orgId, appId);

            console.log("count for payment", response);

            if (response.success && response.data && response.data.length > 0) {
                const proceedTime = new Date(response.data[0].ProceedDateTime);
                const currentTime = new Date();

                // Add 40 minutes
                const allowedTime = new Date(proceedTime.getTime() + 40 * 60000);

                console.log("Proceed Time:", proceedTime);
                console.log("Allowed Time:", allowedTime);
                console.log("Current Time:", currentTime);

                if (currentTime >= allowedTime) {
                    setCanProceedToPay(true);
                } else {
                    setCanProceedToPay(false);

                    // OPTIONAL: auto-enable after remaining time
                    const remainingTime = allowedTime - currentTime;

                    setTimeout(() => {
                        setCanProceedToPay(true);
                    }, remainingTime);
                }
            } else {
                // If no record → allow payment
                setCanProceedToPay(true);
            }
        } catch (error) {
            console.error("Error checking payment count:", error);
            setCanProceedToPay(true);
        }
    };





    // Auto-save when data changes (only if onUpdate is provided and data is meaningful)
    useEffect(() => {
        if (onUpdate && initialLoadComplete && Object.keys(declarationData).length > 0) {
            onUpdate(declarationData);
        }
    }, [declarationData, onUpdate, initialLoadComplete]);

    // Show notification helper
    const showNotification = (message, severity = 'info') => {
        setNotification({
            open: true,
            message,
            severity
        });
    };

    // Close notification
    const closeNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    // Handle checkbox changes
    const handleCheckboxChange = (fieldName) => (event) => {
        setDeclarationData(prev => ({
            ...prev,
            [fieldName]: event.target.checked
        }));
    };

    // Handle text field changes
    const handleTextFieldChange = (fieldName) => (event) => {
        setDeclarationData(prev => ({
            ...prev,
            [fieldName]: event.target.value
        }));
    };

    // Handle file upload - matching Laboratory section implementation
    const handleFileUpload = (fieldName, uploadData) => {
        if (uploadData === null || uploadData === undefined) {
            // File was deleted, remove from form data
            setDeclarationData(prev => ({
                ...prev,
                [fieldName]: null
            }));
            showNotification('Declaration file removed', 'info');
            return;
        }

        const fileData = {
            ...uploadData,
            fileName: uploadData.fileName || uploadData.originalName,
            filePath: uploadData.filePath || uploadData.url,
            uploadDate: new Date().toISOString(),
            fileSize: uploadData.fileSize,
            mimeType: uploadData.mimeType
        };

        setDeclarationData(prev => ({
            ...prev,
            [fieldName]: fileData
        }));

        if (uploadData && uploadData.fileName) {
            showNotification('Declaration file uploaded successfully', 'success');
        }

    };

    // Check if all required fields are filled for saving declaration data
    const canSaveDeclaration = () => {
        return declarationData.representativeName?.trim() &&
            declarationData.representativeTitle?.trim() &&
            declarationData.finalDeclarationAgreed &&
            declarationData.termchk &&
            declarationData.shareholderDirectorFile;
    };

    // Watch for changes in form data to enable/disable save button
    useEffect(() => {
        if (showSaveButton && !fieldsDisabled) {
            // Form is editable - update save button state based on completion
            const isComplete = canSaveDeclaration();
            // Save button will be enabled/disabled based on canSaveDeclaration()
        }
    }, [declarationData, showSaveButton, fieldsDisabled]);

    // Handle declaration submission
    const handleSubmitDeclaration = async () => {
        try {
            setLoading(true);

            // Basic validation - check if all required fields are filled
            if (!canSaveDeclaration()) {
                showNotification('Please complete all required declarations before submitting', 'error');
                setLoading(false);
                return;
            }

            const appId = getAppId();
            const orgId = getOrgId();
            const labtypeidfromstorage = localStorage.getItem('labTypeId')

            // Validate session data
            if (!appId || !orgId) {
                showNotification('Session data not found. Please login again.', 'error');
                setLoading(false);
                return;
            }

            // First submit the final declaration

            showNotification('Submitting application...', 'info');

            const result = await declarationService.submitFinalDeclaration(appId, orgId, labtypeidfromstorage);

            if (result.success) {
                showNotification('Application Submitted Successfully', 'success');
                setSubmitDialog(false);



                // Update parent component if needed
                if (onUpdate) {
                    onUpdate({
                        ...declarationData,
                        isSubmitted: true,
                        submissionResult: result.data
                    });
                }


                showNotification('Redirecting to generate preview report...', 'info');

                // Add a small delay to ensure the user sees the success message
                setTimeout(() => {
                    navigate(`/main/calibration-preview-report?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&observation=${encryptText('False')}&autoDownload=true&fromSubmission=true`);
                }, 1500);

                return;
            } else {
                throw new Error(result.message || 'Failed to submit declaration');
            }

        } catch (error) {
            console.error('Error submitting declaration:', error);
            showNotification(`Error submitting declaration: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle save declaration data
    const handleSaveDeclaration = async () => {
        try {
            setLoading(true);

            // Validate required fields before saving
            if (!canSaveDeclaration()) {
                const missingFields = [];
                if (!declarationData.representativeName?.trim()) missingFields.push('Representative Name');
                if (!declarationData.representativeTitle?.trim()) missingFields.push('Representative Title');
                if (!declarationData.finalDeclarationAgreed) missingFields.push('Final Declaration Agreement');
                if (!declarationData.termchk) missingFields.push('Terms & Conditions Agreement');
                if (!declarationData.shareholderDirectorFile) missingFields.push('Shareholders and Directors Declaration File');

                showNotification(`Please complete the following required fields: ${missingFields.join(', ')}`, 'error');
                return;
            }

            const orgId = getOrgId();
            const appId = getAppId();

            // Combine name and title into DeclNameOrg field
            const combinedNameTitle = `${declarationData.representativeName.trim()}, ${declarationData.representativeTitle.trim()}`;

            const saveData = {
                DeclNameOrg: combinedNameTitle,
                representativeName: declarationData.representativeName.trim(),
                representativeTitle: declarationData.representativeTitle.trim(),
                shareholderDirectorFile: declarationData.shareholderDirectorFile,
                termchk: declarationData.termchk,
                finalDeclarationAgreed: declarationData.finalDeclarationAgreed
            };

            const response = await LaboratoryService.saveDeclarationData(orgId, appId, saveData);

            if (response.success) {
                showNotification('Declaration data saved successfully', 'success');

                // After successful save, reload declaration data to update UI
                await loadDeclarationData();
            } else {
                showNotification(`Error saving declaration data: ${response.message}`, 'error');
            }

        } catch (error) {
            console.error('Error saving declaration data:', error);
            showNotification(`Error saving declaration data: ${error.message}`, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle preview report button click
    const handlePreviewReport = () => {
        const orgId = getOrgId();
        const appId = getAppId();

        if (!orgId || !appId) {
            showNotification('Organization ID or Application ID not found', 'error');
            return;
        }

        // Encrypt parameters before constructing URL
        const encryptedAppId = encryptAppId(appId);
        const encryptedOrgId = encryptText(orgId);
        const encryptedObservation = encryptText('false');

        const previewUrl = `/main/calibration-preview-report?appId=${encryptedAppId}&orgId=${encryptedOrgId}&observation=${encryptedObservation}`;

        // Open in new tab/window
        window.open(previewUrl, '_blank');

        showNotification('Opening preview report in new tab', 'info');
    };

    // Handle proceed to payment button click
    const handleProceedToPay = () => {
        setPasswordDialog(true);
        setPassword('');
        setPasswordError('');
    };

    const handlePerformaInvoice = () => {
        const orgId = localStorage.getItem('orgId');
        const appId = getAppId();


        const previewUrl = `https://nablwpnew.qci.org.in/InvoiceApplicationPerfomaQR?appId=${appId}&payType=2`;

        // Open in new tab/window
        window.open(previewUrl, '_blank');

    };
    // Handle password verification using login password
    const handlePasswordVerification = async () => {
        try {
            setLoading(true);
            const orgId = getOrgId();

            if (!orgId) {
                setPasswordError('Organization ID not found. Please login again.');
                setLoading(false);
                return;
            }

            // Verify password using authService (matches your ASP.NET verifyPass method)
            const verificationResult = await paymentService.verifyPaymentPassword(orgId, password);


            if (verificationResult && verificationResult.success && verificationResult.isValid) {
                setPasswordDialog(false);
                setPasswordError(''); // Clear any previous errors

                // Navigate to payment page directly
                const encryptedPaymentType = encryptText('2');
                navigate(`/main/calibration/payment?paymentType=${encryptedPaymentType}&appId=${encryptAppId(getAppId())}`);

                showNotification('Password verified. Redirecting to payment gateway...', 'success');
            } else {
                // Handle password verification failure
                const errorMessage = verificationResult?.message || 'Password is incorrect';
                setPasswordError(errorMessage);


            }
        } catch (error) {
            console.error('Password verification error:', error);

            // Handle different types of errors
            if (error.response && error.response.status === 500) {
                setPasswordError('Server error. Please try again later.');
            } else if (error.response && error.response.status === 404) {
                setPasswordError('User not found. Please check your organization ID.');
            } else if (error.message && error.message.includes('Network')) {
                setPasswordError('Network error. Please check your connection.');
            } else {
                setPasswordError('Password verification failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleGstExemption = async () => {
        try {
            setLoading(true);

            const appId = getAppId();
            if (!appId) return;

            const result = await LaboratoryService.gstexemption(appId);

            if (result?.success) {
                setGstExemptionStatus('pending');
                setGstExemptionMessage('Your GST exemption request has been submitted and is awaiting approval from the accounts section.');

                showNotification('GST exemption request submitted successfully', 'success');
            } else {
                showNotification('Failed to submit GST exemption request', 'error');
            }

        } catch (error) {
            console.error('GST exemption api not working', error);
            showNotification('Error submitting GST exemption request', 'error');
        } finally {
            setLoading(false);
        }
    };

    // Handle password dialog close
    const handlePasswordDialogClose = () => {
        setPasswordDialog(false);
        setPassword('');
        setPasswordError('');
    };

    return (
        <Box sx={{ p: 3 }}>
            <Paper elevation={3} sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Typography variant="h4" component="h1" sx={{
                        fontWeight: 'bold',
                        color: 'primary.main',
                        mb: 2
                    }}>
                        Declaration for NABL Accreditation
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                        Terms & Conditions for Obtaining and Maintaining NABL Accreditation
                    </Typography>

                    {/* Completion Status Indicator */}
                    <Alert severity="success" sx={{ maxWidth: '600px', mx: 'auto' }}>
                        <Typography variant="body2">
                            ✅ All enclosure requirements have been completed. You can now proceed with the declaration.
                        </Typography>
                    </Alert>
                </Box>

                {/* Adequacy Check Section */}
                <Box sx={{ mb: 4 }}>
                    <AdequacyCheckSection
                        sectionId="declaration"
                        title="Declaration Section"
                        applicationStage={applicationStage}
                        orgId={getOrgId()}
                        appId={getAppId()}
                        adequacyChecks={adequacyChecks}
                        handleAdequacyCheck={handleAdequacyCheck}
                        handleAdequacyRemarks={handleAdequacyRemarks}
                        handleAdequacySubmit={handleAdequacySubmit}
                        loading={adequacyLoading}
                        currentUser={localStorage.getItem('userName') || 'System User'}
                        currentUserId={parseInt(localStorage.getItem('userId') || '0')}
                    />
                </Box>

                {/* Terms & Conditions Content */}
                <Card elevation={2} sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            <GavelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Terms & Conditions
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                            The terms and conditions mentioned in this document is considered as accreditation agreement between NABL and Conformity Assessment Body (CAB). By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions (Accreditation agreement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                            The Conformity Assessment Body {'{Testing Laboratory/ Calibration Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider (PTP)/ Reference Material Producer (RMP)}'} that are applicant or accredited by NABL shall be required to fulfill the following terms and conditions:
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                            By accepting these terms and conditions, it is implied that a CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for Obtaining and Maintaining NABL Accreditation. Any violation of this terms and conditions (arrangement) shall result in adverse decision as per NABL 216 'Procedures for Dealing with Adverse Decisions' including legal action.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'justify', lineHeight: 1.6, fontWeight: 'bold', color: 'error.main' }}>
                            Dispute, if any, arising out of NABL decisions that remain unresolved through mechanism provided by NABL are subject to the exclusive jurisdiction of the Courts at New Delhi and none other.
                        </Typography>
                    </CardContent>
                </Card>

                {/* Declaration by the Laboratory */}
                <Card elevation={2} sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Declaration by the Laboratory
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ fontWeight: 'bold', mb: 3 }}>
                            We declare that:
                        </Typography>

                        <List sx={{ pl: 2 }}>
                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We are familiar with the Terms & Conditions for Obtaining and Maintaining NABL Accreditation (NABL 131), which is enclosed and will abide by it. We have understood the requirements given in NABL 133.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We will maintain strict confidentiality of personal sensitive data. This information will not be shared with any other person, agency or organization (unless required by law).
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We have declared all sources of samples.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We have declared and submitted accurate and complete information in the enclosed "Declaration Form"
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We have conducted internal audit of our Sample Collection Centre(s)/ facility(ies) at least once during the last one year.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We agree to comply fully with ISO/IEC 17025: 2017 and relevant specific criteria for the accreditation of testing laboratory and associated Sample Collection Centre(s)/facility(ies).
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We agree to comply with accreditation procedures, pay all costs for pre-assessment (if applicable), assessment, supplementary visit (if any), surveillance and reassessment irrespective of the result.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We agree to co-operate with the assessment team appointed by NABL for examination of all relevant documents/records by them and their visits to those parts of the laboratory that are part of the scope of accreditation.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        We comply to all national, regional and local regulatory requirements for operating a laboratory.
                                    </Typography>
                                </ListItemText>
                            </ListItem>

                            <ListItem sx={{ display: 'list-item', listStyleType: 'disc', pl: 0 }}>
                                <ListItemText>
                                    <Typography variant="body1" sx={{ textAlign: 'justify', lineHeight: 1.6 }}>
                                        All information provided in this application is true.
                                    </Typography>
                                </ListItemText>
                            </ListItem>
                        </List>

                        <Typography variant="body1" paragraph sx={{ mt: 2, fontStyle: 'italic' }}>
                            Self-declaration shall be submitted by the laboratory on the basis of the internal audit conducted by them, to confirm to NABL that their Sample Collection Centre(s)/ facility(ies) are complying with NABL norms and relevant clauses of ISO/IEC 17025.
                        </Typography>
                    </CardContent>
                </Card>

                {/* Authorized Representative */}
                <Card elevation={2} sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Authorized Representative
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Representative Name"
                                    value={declarationData.representativeName}
                                    onChange={handleTextFieldChange('representativeName')}
                                    variant="outlined"
                                    required
                                    disabled={fieldsDisabled}
                                    error={!fieldsDisabled && !declarationData.representativeName?.trim()}
                                    helperText={!fieldsDisabled && !declarationData.representativeName?.trim() ? 'Representative name is required' : ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Representative Title"
                                    value={declarationData.representativeTitle}
                                    onChange={handleTextFieldChange('representativeTitle')}
                                    variant="outlined"
                                    required
                                    disabled={fieldsDisabled}
                                    error={!fieldsDisabled && !declarationData.representativeTitle?.trim()}
                                    helperText={!fieldsDisabled && !declarationData.representativeTitle?.trim() ? 'Representative title is required' : ''}
                                />
                            </Grid>
                        </Grid>

                        <Typography variant="body1" paragraph sx={{ mt: 2, textAlign: 'justify' }}>
                            <strong>{declarationData.representativeName}</strong>, <strong>{declarationData.representativeTitle}</strong>,
                            authorised representative of CAB has filled/ verified the contents of the application and accepted the above mentioned terms and conditions on behalf of CAB.
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ fontWeight: 'bold' }}>
                            All information provided in this application are true.
                        </Typography>
                    </CardContent>
                </Card>

                {/* File Upload Section */}
                <Card elevation={2} sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                            <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Upload Declaration by the Laboratory Shareholders and Directors *
                        </Typography>

                        <Alert severity={declarationData.shareholderDirectorFile ? 'success' : 'warning'} sx={{ mb: 2 }}>
                            {declarationData.shareholderDirectorFile
                                ? 'Declaration file uploaded successfully.'
                                : 'Please upload the declaration signed by laboratory shareholders and directors. This is required.'
                            }
                        </Alert>

                        <FileUploadComponent
                            fieldName="shareholderDirectorFile"
                            label="Declaration by Shareholders and Directors"
                            orgId={getOrgId()}
                            section="declaration-documents"
                            required={true}
                            accept=".pdf,.jpg,.jpeg,.png"
                            maxSize={10}
                            multiple={false}
                            onChange={handleFileUpload}
                            initialFile={declarationData.shareholderDirectorFile}
                            disabled={fieldsDisabled}
                        />

                        <Box sx={{ mt: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                size="small"
                                onClick={() => {
                                    // Download the Declaration Form from public folder
                                    const link = document.createElement('a');
                                    link.href = `${process.env.PUBLIC_URL}/Declaration Form.docx`;
                                    link.download = 'Declaration Form.docx';
                                    link.target = '_blank';
                                    document.body.appendChild(link);
                                    link.click();
                                    document.body.removeChild(link);
                                    showNotification('Downloading Declaration Form...', 'info');
                                }}
                            >
                                Download Format
                            </Button>
                        </Box>
                    </CardContent>
                </Card>



                {/* Final Declaration */}
                <Card elevation={2} sx={{
                    mb: 4,
                    backgroundColor: declarationData.finalDeclarationAgreed ? 'success.50' : 'warning.50',
                    borderLeft: '5px solid',
                    borderLeftColor: declarationData.finalDeclarationAgreed ? 'success.main' : 'warning.main'
                }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom sx={{
                            color: declarationData.finalDeclarationAgreed ? 'success.dark' : 'warning.dark',
                            fontWeight: 'bold'
                        }}>
                            <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Final Agreement *
                        </Typography>

                        {!declarationData.finalDeclarationAgreed && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                You must agree to the terms and conditions to proceed with saving the declaration.
                            </Alert>
                        )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={declarationData.termchk}
                                    onChange={handleCheckboxChange('termchk')}
                                    color={declarationData.termchk ? "success" : "warning"}
                                    required
                                    disabled={fieldsDisabled}
                                />
                            }
                            label="I have read and understood all the terms and conditions mentioned above."
                            sx={{ alignItems: 'flex-start', mb: 2 }}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={declarationData.finalDeclarationAgreed}
                                    onChange={handleCheckboxChange('finalDeclarationAgreed')}
                                    color={declarationData.finalDeclarationAgreed ? "success" : "warning"}
                                    required
                                    disabled={fieldsDisabled}
                                />
                            }
                            label="By accepting the above terms & conditions, it is implied that a CAB/ Sample Collection Centre/ Facility (ies) declared by medical CAB as an applicant and after obtaining accreditation agrees to comply at all times with all Terms and Conditions for NABL Accreditation. Any violation of this terms and conditions shall result in adverse decision such as abeyance, suspension, forced withdrawal etc against the CAB as specified in 'NABL 216'."
                            sx={{ alignItems: 'flex-start' }}
                        />
                    </CardContent>
                </Card>



                {/* Action Buttons - Simplified Logic */}
                {(() => {
                    const shouldShowButtons = (applicationStage === null || applicationStage === "18a") &&
                        (isGstExemption === false || gstExemptionStatus === 'approved');
                    return shouldShowButtons;
                })() && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>

                            {/* Save Declaration Button - Show when fields are not complete or not saved yet */}
                            {showSaveButton && (
                                <Button
                                    variant="contained"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSaveDeclaration}
                                    disabled={loading || !canSaveDeclaration()}
                                    size="large"
                                    color="primary"
                                    sx={{
                                        background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                                        boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                                    }}
                                >
                                    {loading ? 'Saving...' : 'Save Declaration'}
                                </Button>
                            )}

                            {showPaymentButtons && (
                                <>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ViewIcon />}
                                        onClick={handlePreviewReport}
                                        disabled={loading}
                                        size="large"
                                        color="secondary"
                                        sx={{
                                            borderColor: 'secondary.main',
                                            '&:hover': {
                                                borderColor: 'secondary.dark',
                                                backgroundColor: 'secondary.50'
                                            }
                                        }}
                                    >
                                        Preview Report
                                    </Button>

                                    {canProceedToPay && (
                                        <Button
                                        variant="contained"
                                        startIcon={<PaymentIcon />}
                                        onClick={handleProceedToPay}
                                        disabled={loading}
                                        size="large"
                                        color="success"
                                        sx={{
                                            background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                                            boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
                                        }}
                                    >
                                        Proceed to Pay
                                    </Button>
                                    )}
                                    {!canProceedToPay && (
                                        <Typography color="error">
                                           You will be able to make the payment after 40 minutes of your last attempt.
                                        </Typography>
                                    )}
                                    <Button
                                        variant="outlined"
                                        startIcon={<ViewIcon />}
                                        onClick={handlePerformaInvoice}
                                        disabled={loading}
                                        size="large"
                                        color="primary"
                                        sx={{
                                            borderColor: 'primary.main',
                                            '&:hover': {
                                                borderColor: 'primary.dark',
                                                backgroundColor: 'primary.50'
                                            }
                                        }}
                                    >
                                        Proforma Invoice
                                    </Button>

                                </>
                            )}
                            {/* Submit Final Declaration Button - Show when payment is successful */}
                            {showSubmitButton && (
                                <Button
                                    variant="contained"
                                    startIcon={<SendIcon />}
                                    onClick={() => setSubmitDialog(true)}
                                    disabled={loading}
                                    size="large"
                                    color="success"
                                    sx={{
                                        background: 'linear-gradient(45deg, #FF6B6B 30%, #4ECDC4 90%)',
                                        boxShadow: '0 3px 5px 2px rgba(76, 175, 80, .3)',
                                    }}
                                >
                                    Final Submit
                                </Button>
                            )}

                        </Box>
                    )}
                {/* GST Exemption Status Alert */}
                {(gstExemptionStatus === 'pending' || gstExemptionStatus === 'rejected') && (
                    <Alert
                        severity={gstExemptionStatus === 'rejected' ? 'error' : 'warning'}
                        sx={{
                            mb: 4,
                            p: 3,
                            backgroundColor: gstExemptionStatus === 'rejected' ? '#ffebee' : '#fff3cd',
                            border: `1px solid ${gstExemptionStatus === 'rejected' ? '#f48fb1' : '#ffeaa7'}`,
                            borderRadius: 2,
                            '& .MuiAlert-icon': {
                                fontSize: '2rem'
                            }
                        }}
                    >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 'bold',
                                color: gstExemptionStatus === 'rejected' ? '#c62828' : '#856404'
                            }}>
                                {gstExemptionStatus === 'rejected' ? '❌ GST Exemption Rejected' : '⏳ GST Exemption Approval Pending'}
                            </Typography>
                            <Typography variant="body1" sx={{
                                color: gstExemptionStatus === 'rejected' ? '#c62828' : '#856404'
                            }}>
                                {gstExemptionMessage}
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: gstExemptionStatus === 'rejected' ? '#c62828' : '#856404',
                                fontStyle: 'italic',
                                mt: 1
                            }}>
                                {gstExemptionStatus === 'rejected'
                                    ? 'Please resubmit your GST exemption request with the necessary corrections based on the remarks above.'
                                    : 'Please wait for the accounts section to approve your GST exemption request before proceeding with the application submission.'
                                }
                            </Typography>
                        </Box>
                    </Alert>
                )}

                {/* GST Exemption Approved Status */}
                {gstExemptionStatus === 'approved' && (
                    <Alert
                        severity="success"
                        sx={{
                            mb: 4,
                            p: 2,
                            backgroundColor: '#e8f5e8',
                            border: '1px solid #4caf50',
                            borderRadius: 2
                        }}
                    >
                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                            ✅ GST Exemption Approved
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#2e7d32' }}>
                            Your GST exemption has been approved. You can proceed with the application.
                        </Typography>
                    </Alert>
                )}
                {isGstExemption === true && (gstExemptionStatus === 'pending' || gstExemptionStatus === 'rejected' || gstExemptionStatus === 'not_requested') && (
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGstExemption}
                        sx={{ mt: 2 }}
                    >
                        Proceed for GST Exemption
                    </Button>
                )}
                {isGstExemption === true && gstExemptionStatus === 'pending' && (
                    <Alert severity="warning" sx={{ mt: 3 }}>
                        ⏳ Your GST exemption request has been submitted and is awaiting approval from the accounts section.
                    </Alert>
                )}
                {/* GST Exemption Not Approved Message - Only show for labs that need GST exemption */}
                {isGstExemption === true && (gstExemptionStatus === 'pending' || gstExemptionStatus === 'rejected' || gstExemptionStatus === 'not_requested') && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                        <Alert
                            severity={gstExemptionStatus === 'rejected' ? 'error' : 'info'}
                            sx={{
                                maxWidth: '600px',
                                p: 3,
                                backgroundColor: gstExemptionStatus === 'rejected' ? '#ffebee' : '#e3f2fd',
                                border: `1px solid ${gstExemptionStatus === 'rejected' ? '#f48fb1' : '#90caf9'}`,
                                borderRadius: 2
                            }}
                        >
                            <Typography variant="body1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                                {gstExemptionStatus === 'rejected' ? '❌ Application Submission Pending' : '⏳ Application Submission Disabled'}
                            </Typography>
                            <Typography variant="body2" sx={{ textAlign: 'center', mt: 1 }}>
                                {gstExemptionStatus === 'rejected'
                                    ? 'Your GST exemption request has been rejected. Please resubmit with corrections before proceeding.'
                                    : gstExemptionStatus === 'pending'
                                        ? 'All submission options are currently unavailable until GST exemption is approved by the accounts section.'
                                        : 'Please click on a Proceed for GST exemption request first before proceeding with the application.'
                                }
                            </Typography>
                        </Alert>
                    </Box>
                )}


            </Paper>

            {/* Password Verification Dialog */}
            <Dialog open={passwordDialog} onClose={handlePasswordDialogClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    backgroundColor: 'primary.main',
                    color: 'white'
                }}>
                    <LockIcon />
                    Payment Authorization Required
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Please enter the authorization password to proceed with payment:
                    </Typography>
                    <TextField
                        fullWidth
                        type="password"
                        label="Login Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        error={!!passwordError}
                        helperText={passwordError || 'Enter your login password to authorize payment'}
                        variant="outlined"
                        disabled={loading}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !loading) {
                                handlePasswordVerification();
                            }
                        }}
                        autoFocus
                    />
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            Enter the same password you use to login to your account.
                            This ensures secure access to the payment gateway.
                        </Typography>
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={handlePasswordDialogClose}
                        color="inherit"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handlePasswordVerification}
                        variant="contained"
                        disabled={!password.trim() || loading}
                    >
                        {loading ? 'Verifying...' : 'Verify & Proceed'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Submit Confirmation Dialog */}
            <Dialog open={submitDialog} onClose={() => setSubmitDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Submit Final Declaration</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to submit the final declaration? This action confirms your agreement to all terms and conditions.
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Once submitted, you will not be able to modify the declaration.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSubmitDialog(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmitDeclaration}
                        variant="contained"
                        color="success"
                        disabled={loading}
                    >
                        {loading ? 'Submitting...' : 'Submit Declaration'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notification Snackbar */}
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={closeNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert
                    onClose={closeNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default DeclarationSection;